/**
 * Arabise audio — ported from legacy js/audio.js.
 *
 * Three-stage fallback chain when speaking Arabic:
 *   1. Browser Arabic voice (Google ar-SA, Microsoft Hoda, Apple Majed,
 *      eSpeak — anything tagged ar-* by the OS).
 *   2. /api/tts worker (Cloudflare Worker that proxies Microsoft Edge
 *      Read-Aloud, MP3 cached in R2 for ~10ms edge replay).
 *   3. Transliteration via the English voice — sounds approximately
 *      right ("al-bayt") when Arabic voices are unavailable.
 *
 * Safari refuses to speak before the first user gesture; we mark
 * the gesture flag from any pointer/touch/keydown. Rapid successive
 * calls cancel the prior utterance so you never hear two words at once.
 *
 * The TTS worker URL is configurable via VITE_TTS_URL — defaults to
 * the production endpoint. Set to '' to disable the remote fallback.
 */

const SUPPORTED = typeof window !== "undefined" && "speechSynthesis" in window;
const TTS_BASE  = (import.meta.env.VITE_TTS_URL as string | undefined)
              ?? "https://arabise.co.uk/api/tts";
const TTS_VOICE = "ar-SA-ZariyahNeural";

const IS_SAFARI = typeof navigator !== "undefined" &&
  /^((?!chrome|android|crios|fxios|edg).)*safari/i.test(navigator.userAgent || "");

let voicesLoaded = false;
let cachedVoice:    SpeechSynthesisVoice | null = null;   // English / default
let cachedArVoice:  SpeechSynthesisVoice | null = null;   // Arabic
let userGestureSeen = false;
let currentAutoAudio: HTMLAudioElement | null = null;

if (typeof window !== "undefined") {
  const markGesture = () => { userGestureSeen = true; };
  ["pointerdown", "touchstart", "keydown", "click"].forEach(evt => {
    window.addEventListener(evt, markGesture, { capture: true, passive: true });
  });
}

if (SUPPORTED) {
  pickVoice();
  window.speechSynthesis.addEventListener("voiceschanged", pickVoice);
  let pollsLeft = 5;
  const poll = setInterval(() => {
    pickVoice();
    if (voicesLoaded || --pollsLeft <= 0) clearInterval(poll);
  }, 500);
}

function pickVoice() {
  if (!SUPPORTED) return;
  const voices = window.speechSynthesis.getVoices() || [];
  if (voices.length === 0) return;
  voicesLoaded = true;

  // English voice preferences: en-GB local > en-US local > anything en > anything.
  const enRanked: ((v: SpeechSynthesisVoice) => boolean)[] = [
    v => /^en[-_]gb$/i.test(v.lang) && v.localService,
    v => /^en[-_]us$/i.test(v.lang) && v.localService,
    v => /^en[-_]/i.test(v.lang),
    () => true
  ];
  for (const t of enRanked) { const m = voices.find(t); if (m) { cachedVoice = m; break; } }
  if (!cachedVoice) cachedVoice = voices[0];

  const isAr = (v: SpeechSynthesisVoice) => /^ar(?:$|[-_])/i.test(v.lang || "");
  const arRanked: ((v: SpeechSynthesisVoice) => boolean)[] = [
    v => isAr(v) && /sa/i.test(v.lang) && v.localService,
    v => isAr(v) && /eg/i.test(v.lang) && v.localService,
    v => isAr(v) && v.localService,
    v => isAr(v) && /sa/i.test(v.lang),
    v => isAr(v),
    v => /\bar\b|arabic|عربي|majed|tarik|hoda|naayf|salim|zariyah/i.test(
      ((v.name || "") + " " + (v.lang || ""))
    )
  ];
  cachedArVoice = null;
  for (const t of arRanked) { const m = voices.find(t); if (m) { cachedArVoice = m; break; } }
}

function waitForVoices(maxMs = 600): Promise<void> {
  if (!SUPPORTED) return Promise.resolve();
  return new Promise(resolve => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      try { window.speechSynthesis.removeEventListener("voiceschanged", finish); } catch {}
      resolve();
    };
    const n = (window.speechSynthesis.getVoices() || []).length;
    if (n > 0) return finish();
    try { window.speechSynthesis.addEventListener("voiceschanged", finish); } catch {}
    const start = Date.now();
    (function poll() {
      if (done) return;
      if ((window.speechSynthesis.getVoices() || []).length > 0) return finish();
      if (Date.now() - start >= maxMs) return finish();
      setTimeout(poll, 80);
    })();
  });
}

function sanitiseForSpeech(raw: string): string {
  return String(raw || "")
    .replace(/[\/\\\-\(\)\[\]{}]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Speak romanised transliteration via the English voice. */
export function speakTransliteration(text: string): SpeechSynthesisUtterance | null {
  if (!SUPPORTED) return null;
  const value = sanitiseForSpeech(text);
  if (!value) return null;
  userGestureSeen = true;
  try { window.speechSynthesis.cancel(); } catch {}
  const u = new SpeechSynthesisUtterance(value);
  u.lang   = (cachedVoice && cachedVoice.lang) || "en-GB";
  if (cachedVoice) u.voice = cachedVoice;
  u.rate   = 0.92; u.pitch = 1.0; u.volume = 1.0;
  try { window.speechSynthesis.speak(u); } catch { return null; }
  return u;
}

/** Speak Arabic glyphs using the cached Arabic voice (local engine). */
function speakArabicLocal(arabicText: string): boolean {
  if (!SUPPORTED || !cachedArVoice) return false;
  const value = String(arabicText || "")
    .replace(/[\(\)\[\]{}]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!value) return false;
  userGestureSeen = true;
  try { window.speechSynthesis.cancel(); } catch {}
  const u = new SpeechSynthesisUtterance(value);
  u.voice = cachedArVoice; u.lang = cachedArVoice.lang || "ar-SA";
  u.rate = 0.88; u.pitch = 1.0; u.volume = 1.0;
  try { window.speechSynthesis.speak(u); } catch { return false; }
  return true;
}

/** Try the Arabic voice and verify it actually starts (within 700 ms). */
function tryBrowserArabic(arabicText: string): Promise<boolean> {
  return new Promise(resolve => {
    if (!cachedArVoice || !arabicText) return resolve(false);
    const value = String(arabicText)
      .replace(/[\(\)\[\]{}]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    if (!value) return resolve(false);

    userGestureSeen = true;
    try { window.speechSynthesis.cancel(); } catch {}

    const u = new SpeechSynthesisUtterance(value);
    u.voice = cachedArVoice; u.lang = cachedArVoice.lang || "ar-SA";
    u.rate = 0.88; u.pitch = 1.0; u.volume = 1.0;

    let settled = false;
    const ok   = () => { if (!settled) { settled = true; resolve(true); } };
    const fail = () => { if (!settled) { settled = true; resolve(false); } };
    const startGuard = window.setTimeout(fail, 700);

    u.onstart = () => { window.clearTimeout(startGuard); ok(); };
    u.onerror = () => { window.clearTimeout(startGuard); fail(); };
    try { window.speechSynthesis.speak(u); } catch { window.clearTimeout(startGuard); fail(); }
  });
}

/** Try the remote TTS worker. Returns true once playback starts. */
function tryRemoteArabic(arabic: string): Promise<boolean> {
  return new Promise(resolve => {
    if (!arabic || !TTS_BASE) return resolve(false);
    if (currentAutoAudio) { try { currentAutoAudio.pause(); } catch {} currentAutoAudio = null; }

    const sep = TTS_BASE.includes("?") ? "&" : "?";
    const url = `${TTS_BASE}${sep}text=${encodeURIComponent(arabic)}&voice=${encodeURIComponent(TTS_VOICE)}`;
    const audio = new Audio(url);
    audio.preload = "auto";
    currentAutoAudio = audio;

    let settled = false;
    audio.addEventListener("playing", () => { if (!settled) { settled = true; resolve(true); } }, { once: true });
    audio.addEventListener("error",   () => { if (!settled) { settled = true; resolve(false); } }, { once: true });
    audio.addEventListener("ended",   () => { if (currentAutoAudio === audio) currentAutoAudio = null; }, { once: true });

    audio.play().catch(() => { if (!settled) { settled = true; resolve(false); } });
    window.setTimeout(() => { if (!settled) { settled = true; resolve(false); } }, 4000);
  });
}

type Word = { arabic?: string; transliteration?: string; english?: string };

/**
 * Speak a word, preferring native Arabic → remote worker →
 * transliteration. Pass the WORD OBJECT (has arabic + transliteration)
 * so we can choose the right fallback.
 */
export async function speakWord(word: Word): Promise<boolean> {
  if (!word) return false;

  // Honour autoplay opt-out for auto-fired playback.
  try { if (localStorage.getItem("arabise-autoplay-off") === "1") return false; } catch {}

  if (IS_SAFARI && !userGestureSeen) return false;

  // Make sure voices have a chance to load.
  if (!cachedArVoice && SUPPORTED) {
    await waitForVoices(400);
    pickVoice();
  }

  const ar = (word.arabic || "").trim();

  if (ar) {
    if (cachedArVoice) {
      const ok = await tryBrowserArabic(ar);
      if (ok) return true;
      // If the local Arabic voice claimed success above we'd have returned;
      // otherwise fall through to remote.
    } else {
      // No local voice — try to nudge the synth anyway (some engines lie
      // about their voice list and DO render ar text via the default voice).
      const ok = speakArabicLocal(ar);
      if (ok) return true;
    }

    // Remote MP3.
    const ok = await tryRemoteArabic(ar);
    if (ok) return true;
  }

  // Last fall back: transliteration via English voice.
  const tr = (word.transliteration || word.english || "").trim();
  if (tr) {
    speakTransliteration(tr);
    return true;
  }
  return false;
}

/** Imperative speak from a user click — same chain, but fires regardless of autoplay opt-out. */
export async function playWordClick(word: Word): Promise<boolean> {
  let was: string | null = null;
  try {
    was = localStorage.getItem("arabise-autoplay-off");
    if (was) localStorage.removeItem("arabise-autoplay-off");
  } catch {}
  try { return await speakWord(word); }
  finally {
    try { if (was) localStorage.setItem("arabise-autoplay-off", was); } catch {}
  }
}

/** Cancel any in-flight speech / audio. */
export function cancelAudio() {
  try { window.speechSynthesis?.cancel(); } catch {}
  if (currentAutoAudio) { try { currentAutoAudio.pause(); } catch {} currentAutoAudio = null; }
}
